import { useTranslation } from 'react-i18next'
import { Badge, Flex, FormField } from '@percent/lemonade'
import { LocaleKey } from '@percent/cause-dashboard/i18n/config'
import { AgentVerificationDocuments } from '../AgentVerificationDocuments'
import { documentTypesFor } from '../documentTypes'
import { ReuploadViewProps } from './ReuploadView.types'
import styles from './ReuploadView.module.scss'

const DOCUMENT_REQUIREMENTS: LocaleKey[] = [
  'agentVerificationDocuments.reupload.requirements.clear',
  'agentVerificationDocuments.reupload.requirements.fullName',
  'agentVerificationDocuments.reupload.requirements.organisationName',
  'agentVerificationDocuments.reupload.requirements.originalCopies'
]

export const ReuploadView = ({
  agentVerificationToken,
  statusData,
  onDocumentsSubmitted
}: Readonly<ReuploadViewProps>) => {
  const { t } = useTranslation()
  const { id, partnerBanner, attemptsRemaining, organisationName, programType } = statusData
  const documentTypes = documentTypesFor(programType)

  const header = (
    <Flex direction="column" gap={8} data-testid="reupload-active">
      <h5>{t('agentVerificationDocuments.reupload.title')}</h5>
    </Flex>
  )

  const body = (
    <>
      <p>{t('agentVerificationDocuments.reupload.uploadIntro', { organisationName })}</p>

      <FormField label={t('agentVerificationDocuments.reupload.typeOfDocument')} necessity="required">
        <ul className={styles.documentList}>
          {documentTypes.map(({ key, recommended }) => (
            <li key={key} className={styles.documentListItem}>
              {t(key)}
              {recommended && (
                <span className={styles.recommendedBadge}>
                  <Badge variant="positive" size="small">
                    {t('agentVerificationDocuments.reupload.recommended')}
                  </Badge>
                </span>
              )}
            </li>
          ))}
        </ul>
      </FormField>

      <Flex direction="column" gap={8}>
        <p>{t('agentVerificationDocuments.reupload.requirements.title')}</p>
        <ul>
          {DOCUMENT_REQUIREMENTS.map(key => (
            <li key={key}>{t(key)}</li>
          ))}
        </ul>
      </Flex>

      <p data-testid="reupload-note">
        {t('agentVerificationDocuments.note', { organisationName })}{' '}
        {attemptsRemaining === 1
          ? t('agentVerificationDocuments.reupload.finalAttempt')
          : t('agentVerificationDocuments.reupload.attemptsRemaining', { count: attemptsRemaining })}
      </p>
    </>
  )

  return (
    <AgentVerificationDocuments
      agentVerificationId={id}
      agentVerificationToken={agentVerificationToken}
      onDocumentsSubmitted={onDocumentsSubmitted}
      organisationName={organisationName}
      header={header}
      hideDefaultHeading
      body={body}
      useDropzone
      partnerBanner={partnerBanner}
    />
  )
}
